import React, { FunctionComponent, SVGProps } from "react";
import Button from "./Button";
import notFoundImage from "../../assets/images/not-found.png";

export type NotFoundVariant =
  | "posts"
  | "videos"
  | "articles"
  | "saved-jobs"
  | "applied-jobs"
  | "jobs"
  | "users"
  | "searched-jobs"
  | "admin"
  | "candidates";

export const NOT_FOUND_COPY: Record<
  NotFoundVariant,
  { title: string; description: string; monitorText: string }
> = {
  posts: {
    title: "No Post Yet",
    description: "Create your first post to share with the community.",
    monitorText: "No Post",
  },
  videos: {
    title: "No Video Yet",
    description: "Create your first video to share with the community.",
    monitorText: "No Video",
  },
  articles: {
    title: "No Article Yet",
    description: "Create your first article to share with the community.",
    monitorText: "No Article",
  },
  "searched-jobs":{
    title: "No Jobs Found",
    description: "Try adjusting your search to find more opportunities.",
    monitorText: "No Job",
  },
  "saved-jobs": {
    title: "No Save Jobs Yet",
    description: "Your saved jobs will appear here.",
    monitorText: "No Job",
  },
  "applied-jobs": {
    title: "No Applied Jobs Yet",
    description: "Your applied jobs will appear here.",
    monitorText: "No Job",
  },
  jobs: {
    title: "No jobs found",
    description: "Try adjusting your search to find more opportunities.",
    monitorText: "No Job",
  },
  users: {
    title: "No User Yet",
    description: "People you are looking for will appear here.",
    monitorText: "No User",
  },
  admin: {
    title: "No admins found",
    description: "Add admins to level up your employHER network",
    monitorText: "No Admin",
  },
  candidates: {
    title: "No candidate applications found",
    description: "Candidates who apply to your jobs will appear here.",
    monitorText: "No Candidate",
  },
};

interface NotFoundPageProps {
  variant?: NotFoundVariant;
  title?: string;
  content?: string;
  description?: string;
  handleClick?: () => void;
  displayAddNewButton?: boolean;
  buttonText?: string;
  size?: "small" | "medium" | "large";
  monitorText?: string;
  image?: string | FunctionComponent<SVGProps<SVGSVGElement>>;
  className?: string;
}

const NotFoundPage = (props: NotFoundPageProps) => {
  const preset = props.variant ? NOT_FOUND_COPY[props.variant] : undefined;
  const title =
    props.title || props.content || preset?.title || "No jobs found";
  const description =
    props.description !== undefined
      ? props.description
      : preset?.description || "";
  const monitorText = props.monitorText ?? preset?.monitorText ?? "";

  const renderImage = () => {
    if (typeof props.image === "function") {
      const CustomImage = props.image;
      return <CustomImage />;
    }

    return (
      <>
        <img
          src={props.image || notFoundImage}
          alt=""
          className="eh-not-found__image"
        />
        {monitorText ? (
          <span className="eh-not-found__monitor">{monitorText}</span>
        ) : null}
      </>
    );
  };

  return (
    <div
      className={["eh-not-found", props.size && `is-${props.size}`, props.className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="eh-not-found__media">{renderImage()}</div>
      <h3 className="eh-not-found__title">{title}</h3>
      {description ? (
        <p className="eh-not-found__description">{description}</p>
      ) : null}
      {props.displayAddNewButton && (
        <div className="eh-not-found__action">
          <Button variant="outlined" onClick={props.handleClick}>
            {props.buttonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotFoundPage;
